<script>
	const page = require('page');
	const userContext = require('core/userContext');
	const accountService = require('services/accountService');
	const utilities = require('utilities');
	const config = require('core/config');
	const model = require('model/model');
	const { assetUrl } = require('utilities/urlUtilities');
	const pubsub = require('events/pubsub');
	const { getGenericGCAuthURL } = require('utilities/googleClassroomUtilities');

	const protocol = location.protocol;
	const host = config.host;

	const isDev = host.includes('development') || host.includes('localhost') || host.includes('qa.readworks');

	module.exports = {
		props: {
			routeContext: {
				type: Object,
				required: true
			}
		},
		components: {
			'authenticated-student-warning-message': require('components/authenticated-student-warning-message.vue').default,
			'validated-input': require('components/validated-input.vue').default,
			'validated-form': require('components/validated-form.vue').default,
			'teacher-icon': require('components/svg/teacher-icon.vue').default,
			'google-logo': require('components/svg/google-logo.vue').default
		},
		data: function () {
			return {
				email: '',

				password: '',

				optionsURL: null,

				userContext
			};
		},
		computed: {
			signUpURL() {
				let url = '';
				let pathname = this.routeContext.pathname;

				if (pathname && pathname.indexOf('/article') === 0) {
					url = pathname;
				}

				return url;
			},
			cleverUrl() {
				let clientID = config.clever.id;
				let redirectUrl = encodeURIComponent(`${protocol}//${host}/v2/login/clever`);
				let cUrl = `https://clever.com/oauth/authorize?response_type=code&redirect_uri=${redirectUrl}&client_id=${clientID}`;

				if (isDev) {
					cUrl += '&district_id=5b2ad81a709e300001e2cd7a';
				}

				return cUrl;
			},
			cleverLoginImageSrc() {
				return assetUrl('/assets/dist/images/clever-c.png');
			},
			googleUrl() {
				const searchParams = new URLSearchParams({
					role: 'teacher'
				});

				const redirectUrl = this.getRedirectUrl();
				if (redirectUrl) {
					searchParams.set('url', redirectUrl);
				}

				return getGenericGCAuthURL(searchParams);
			}
		},
		methods: {
			clickForgotPassword: function () {
				pubsub.publish('forgot-password', this.email);
			},
			signIn: function () {
				let email = this.email;
				let password = this.password;
				let data = { email, password };
				let pathname = this.routeContext.pathname;
				let redirectUrl = this.getRedirectUrl();

				return accountService
					.teacherSignIn(data)
					.then(result => {
						userContext.initialize(result);

						pubsub.publish('logged-in');

						// Drupal first time log in, has firstName/lastName already
						if (userContext.isFromDrupal()) {
							page.redirect('/my-account#!myAccountTab:about/');
						} else if (redirectUrl && redirectUrl !== pathname) {
							page.redirect(redirectUrl);
						} else if (pathname === '/' || pathname === '/teacher-signup') {
							page.redirect('/');
						}

						this.close();

						return result;
					})
					.catch(err => {
						// Only if first and/or last name is missing
						if (err && err.status === 409 && err.code === 1077) {
							let opts = Object.assign({}, err.data || {}, data);
							utilities.openModal('#drupal-name-modal', opts);
						}

						throw err;
					});
			},
			close: function () {
				utilities.closeModals();
				this.optionsURL = null;
			},
			getRedirectUrl() {
				const routeContext = this.routeContext;
				const pathname = routeContext.pathname;
				const urlQueryParam = routeContext.parsedPath && routeContext.parsedPath.getQueryParam('url');
				const optionsURL = this.optionsURL;

				if (optionsURL) {
					return optionsURL;
				} else if (pathname && urlQueryParam) {
					return urlQueryParam;
				}
				return pathname;
			},
			openStudentLogin() {
				model.remove('loginChoice');
				this.close();
			},
			clickSignUp() {
				model.remove('loginChoice');
				this.close();
			}
		},
		watch: {
			'routeContext.pathname': function () {
				if (document.location.hash.substring(1) === 'login') {
					utilities.openModal('#teacher-login-modal');
					if (history && history.replaceState) {
						history.replaceState(undefined, undefined, '#');
					} else {
						document.location.hash = '#';
					}
				}
			}
		},
		mounted() {
			this.$subscribe('modal-closed', modalId => {
				if (modalId !== 'teacher-login-modal') {
					return;
				}
				this.optionsURL = null;
			});

			this.$subscribe('modal-opened', (modalId, options) => {
				if (modalId !== 'teacher-login-modal') {
					return;
				}
				this.optionsURL = null;
				if (options.url) {
					this.optionsURL = options.url;
				}
			});
		}
	};
</script>
<template>
	<modal
		id="teacher-login-modal"
		class="modal"
		:ok="false"
		:wide="true">
		<template slot="sidebar">
			<div class="sidebar-content">
				<teacher-icon></teacher-icon>
				<p class="not-a-teacher font-small text-centered">
					Not a teacher?
					<a
						href="/student-authentication"
						@click="openStudentLogin"
						>Go to the Student Login</a
					>
				</p>
			</div>
		</template>
		<template slot="header-container">
			<authenticated-student-warning-message v-if="userContext.isStudent()"> </authenticated-student-warning-message>
			<h2>Educator/Parent Log In</h2>
			<div class="not-a-teacher text-centered">
				Not a teacher?
				<a
					href="/student-authentication"
					@click="openStudentLogin"
					>Go to the Student Login</a
				>
			</div>
		</template>
		<template slot="content">
			<div class="flex-spacer">&nbsp;</div>
			<validated-form
				class="sign-in"
				:on-submit="signIn">
				<div class="input-wrapper">
					<validated-input
						type="email"
						id="teacher-login-modal-email"
						class="email"
						:debounce-delay="50"
						override-message="Please enter a valid email"
						v-model.trim="email"
						name="email"
						required
						autocomplete="signup-email"></validated-input>
					<label
						for="teacher-login-modal-email"
						placeholder="Email"
						alt="Email"></label>
				</div>
				<div class="input-wrapper">
					<!-- TODO: enforce stronger password policy on login -->
					<validated-input
						class="password"
						id="teacher-login-modal-password"
						type="password"
						required
						v-model="password"
						minlength="4"
						pattern=".{4,}"
						maxlength="32"
						pattern-message="Passwords must be a minimum of 4 characters"
						name="password"
						autocomplete="new-password"></validated-input>
					<label
						for="teacher-login-modal-password"
						placeholder="Password"
						alt="Password"></label>
				</div>
				<p class="form-row-help">
					<a
						class="text-muted font-small"
						v-on:click="clickForgotPassword">
						Forgot password?
					</a>
				</p>
				<div class="input-wrapper submit-wrapper">
					<button
						type="submit"
						class="button submit -full-width">
						Log In
					</button>
				</div>
			</validated-form>
			<div class="login-or text-muted">or</div>
			<div class="text-centered external-login-buttons">
				<a
					:href="googleUrl"
					class="button -google">
					<google-logo></google-logo>
					<span>Sign in with Google</span>
				</a>
				<a
					:href="cleverUrl"
					class="button -clever">
					<span class="logo-wrapper">
						<img
							:src="cleverLoginImageSrc"
							alt="Clever logo" />
					</span>
					<span>Log In With Clever</span>
				</a>
			</div>
			<div class="flex-spacer">&nbsp;</div>
			<p class="signup-text text-centered">
				Don't have an account yet?
				<a
					:href="`/teacher-signup?url=${signUpURL || ''}`"
					class="sign-up"
					@click="clickSignUp"
					>Sign Up</a
				>.
			</p>
		</template>
	</modal>
</template>
<style lang="scss">
	@import 'config/colors';
	@import 'config/variables';
	@import 'mixins/media';

	#teacher-login-modal {
		p.font-small {
			margin: 0;
			font-weight: normal;
			text-align: center;
		}

		.modal-content-header {
			padding-bottom: 0;
			margin-bottom: 0;
			border-bottom: none;

			.not-a-teacher {
				margin-top: 1rem;

				@include media(med, min) {
					display: none;
				}
			}
		}

		.form-row-help {
			margin: auto;
		}

		.modal-content {
			display: flex;
			flex-direction: column;

			.modal-content-main {
				flex: 1;
				display: flex;
				flex-direction: column;
				padding: 0 1.5rem;

				.flex-spacer {
					flex: 1;
				}
			}
		}

		.sidebar {
			overflow: hidden;
			max-width: 50%;
			background-color: $c-grape-main;

			.sidebar-content {
				min-width: 352px;
				height: 100%;
				position: relative;

				svg {
					position: absolute;
					margin-left: auto;
					margin-right: auto;
					max-width: 100%;
					top: 50%;
					left: 50%;
					transform: translate(-50%, -50%);
				}

				.not-a-teacher {
					right: 0;
					left: 0;
					color: $c-white;
					position: absolute;
					bottom: 0;
					// background-color: $c-white;
					border-radius: 10px;
					a {
						color: $c-blue-light;
						&:hover {
							color: $c-white;
						}
					}
				}
			}
		}

		.login-or {
			text-align: center;
			margin: 0.75rem 0 1.75rem;
			font-weight: bold;
			font-size: 1.25rem;
		}

		p.not-a-teacher,
		p.signup-text {
			font-size: 1rem;
			margin-bottom: 0;
			padding: 0 1rem;
		}

		.external-login-buttons {
			display: flex;
			justify-content: center;
			flex-wrap: wrap;

			a {
				margin: 0 0.5rem 1rem;
			}
		}
	}
</style>
